import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import classNames from 'classnames'
import React from 'react'
import { roboto } from '@/utils/fonts'
import { useGetCustomerQuery } from '@/api/customer'
import useAccountMenu from '@/utils/hooks/useAccountMenu'
import useLogout from '@/utils/hooks/useLogout'
import styles from './AccountDropdown.module.scss'

const toInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()

/**
 * The account entry in the header, with the customer's own pages as a dropdown.
 *
 * Opens on :hover / :focus-within like MenuDropdown rather than on click, so every panel in
 * the row behaves the same way. The avatar stays a link to /account, which keeps the click
 * target the account block has always had and makes the panel reachable by keyboard.
 */
const AccountDropdown = () => {
  const { t } = useTranslation('common')
  const { data: customer } = useGetCustomerQuery()
  const accountMenuItems = useAccountMenu()
  const logout = useLogout()

  if (!customer?.id) {
    return null
  }

  const initials = toInitials(customer.name)
  const accountType = customer.isCompany
    ? t('account.companyAccount')
    : t('account.privateAccount')

  return (
    <li className={styles.component}>
      <Link href="/account" className={styles.trigger}>
        <span className={styles.avatar}>{initials}</span>
        <span className={styles.label}>{t('menu.myProfile')}</span>
      </Link>
      <div className={styles.dropdown}>
        <div className={styles.identity}>
          <span className={styles.avatarLarge}>{initials}</span>
          <span className={styles.customer}>
            <span className={styles.name}>{customer.name}</span>
            <span className={classNames(styles.meta, roboto.className)}>
              {t('account.customerNumber', { number: customer.id })} · {accountType}
            </span>
          </span>
        </div>
        <div className={classNames(styles.links, roboto.className)}>
          {accountMenuItems.map((item) => (
            <Link key={item.url} href={item.url}>
              {item.label}
            </Link>
          ))}
        </div>
        <button type="button" className={styles.logout} onClick={logout}>
          {t('logout')}
        </button>
      </div>
    </li>
  )
}

export default AccountDropdown
